/**
 * Round-5 — pure choreography for the bracket-level follower recall
 * (HGSS convention: the follower returns to its ball for cinematics
 * whose walk geometry could cross its tile).
 *
 * WorldScene.runHandlerBracket owns the Phaser side and calls these
 * two functions at bracket enter / finally with its real hooks; the
 * depth counter makes nested opt-in brackets recall once and restore
 * once, and the species capture makes the restore independent of
 * party mutations mid-bracket. Opt-in per bracket: today only the
 * rival post-battle exit uses it — labPostPick deliberately keeps the
 * starter out (it stands at the player's right for that beat), and
 * the Oak intercept is pre-starter so the idempotence path (no
 * follower out → no-op, no restore) covers it for free.
 *
 * Pure so the once-per-nest / idempotence / restore-on-throw matrix
 * is unit-testable without Phaser (labIntroLockdown pattern).
 */

export interface BracketRecallState {
  /** Count of currently-open OPT-IN brackets (non-opt-in brackets
   *  never touch this). */
  depth: number;
  /** Species captured at recall time; null = nothing to restore. */
  species: string | null;
}

export interface BracketRecallHooks {
  /** Is a follower currently out in the world? */
  isOut(): boolean;
  /** Current lead species (restore target), or null if party empty. */
  leadSpecies(): string | null;
  /** Play the recall (instant under reduce-animations — the hook owns
   *  that read). */
  recall(): void;
  /** Send the follower back out with the Pokéball-pop. */
  restore(species: string): void;
}

/** Call at bracket entry. Recalls only on the OUTERMOST opt-in bracket
 *  and only when a follower is actually out. */
export function enterBracketRecall(
  state: BracketRecallState,
  wantsRecall: boolean,
  hooks: BracketRecallHooks,
): void {
  if (!wantsRecall) return;
  state.depth += 1;
  if (state.depth !== 1) return;
  const species = hooks.leadSpecies();
  if (!hooks.isOut() || !species) return;
  state.species = species;
  hooks.recall();
}

/** Call in the bracket's finally. Restores only when the OUTERMOST
 *  opt-in bracket closes and a recall actually happened — so a thrown
 *  handler still gets its follower back, and a no-follower bracket
 *  restores nothing.
 *
 *  `restore = false` (round-6, battle-launching brackets): the rival
 *  PRE-battle challenge recalls the follower and then launches
 *  straight into BattleScene — a restore pop here would race the
 *  battle fade and get snapped away by SLEEP a frame later. Recall
 *  lands the follower in the exact onSleep() state the battle's own
 *  SLEEP produces, and the post-battle WAKE handler re-derives it
 *  beside the player on every outcome — the wake machinery IS the
 *  restore. Bookkeeping still balances: species is ALWAYS cleared at
 *  outermost close so a later opt-in bracket can never restore from a
 *  stale capture. */
export function exitBracketRecall(
  state: BracketRecallState,
  wantsRecall: boolean,
  hooks: BracketRecallHooks,
  restore: boolean = true,
): void {
  if (!wantsRecall) return;
  state.depth -= 1;
  if (state.depth !== 0 || !state.species) return;
  const species = state.species;
  state.species = null;
  if (restore) hooks.restore(species);
}
